import React, {useState, useMemo, useCallback} from 'react';
import {Col, Dropdown} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Badge, Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import {useAlert} from '@/components/alerts';
import {useStoreActions} from 'easy-peasy';
import axios from 'axios';
import useData from '@/utils/useData';
import Spinner from '@/components/ui/Spinner';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import SearchBox from '@/components/search/SearchBox';
import PermissionDetailModal from '@/components/modals/PermissionDetailModal';
import PermissionRolesModal from '@/components/modals/PermissionRolesModal';
import PermissionEditModal from '@/components/modals/PermissionEditModal';
import SelectField, {SelectOption} from '@/components/forms/fields/SelectField';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {extractErrorMessage} from '@/utils/errorHandling';
import {isModuleEnabled} from '@/utils/modules';

interface Permission {
    id: number;
    name: string;
    codename: string;
    description?: string;
    content_type: string;
    domain: string;
    action: string;
    assigned_roles: string[];
    user_count: number;
}

const PermissionsListView: React.FC = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const {showSuccess, showError} = useAlert();
    const setPermissionsReload = useStoreActions((actions: any) => actions.permissions.setReload);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [domainFilter, setDomainFilter] = useState<SelectOption | null>(null);
    const [roleFilter, setRoleFilter] = useState<SelectOption | null>(null);
    const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
    const [rolesModalOpen, setRolesModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [selectedPermissionId, setSelectedPermissionId] = useState<number | null>(null);
    const [selectedPermissionName, setSelectedPermissionName] = useState<string>('');
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const [permissionsResponse, loadingPermissions] = useData(window.URLS.adminPermissionsList, {
        storeResolver: 'permissions',
        initialValue: {permissions: [], domains: []},
    });

    // Extract permissions array from the API response
    const allPermissions = (permissionsResponse as any)?.permissions || [];
    const apiDomains = (permissionsResponse as any)?.domains || [];

    const canViewPermissions = hasPermission('fs.roles_permissions.permission.list', userData?.permissions, true);
    const canUpdatePermissions = hasPermission('fs.roles_permissions.permission.update', userData?.permissions, true);
    const canExportPermissions = hasPermission('fs.roles_permissions.permission.export', userData?.permissions, true);

    const domains = useMemo(() => {
        return apiDomains.length > 0 ? apiDomains : [];
    }, [apiDomains]);

    const domainOptions = useMemo((): SelectOption[] => {
        return domains.map((domain: string) => ({
            value: domain,
            label: domain.charAt(0).toUpperCase() + domain.slice(1)
        }));
    }, [domains]);

    const roleOptions = useMemo((): SelectOption[] => {
        return [
            {value: 'assigned', label: i18n.t('permissions_assigned_to_roles.text')},
            {value: 'unassigned', label: i18n.t('permissions_unassigned.text')}
        ];
    }, []);

    const handleExportPermissions = useCallback(async () => {
        if (!canExportPermissions) {
            showError(i18n.t('permissions_export_no_permission.text'));
            return;
        }

        setIsExporting(true);
        try {
            const url = buildURL('adminPermissionsExport');

            const response = await axios.get(url, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            });

            // Create download link
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // Extract filename from response headers or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'permissions_matrix.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            showSuccess(i18n.t('permissions_export_success.text'));
        } catch (error) {
            console.error('Export error:', error);
            const errorMessage = extractErrorMessage(error, i18n.t('permissions_export_error.text'));
            showError(errorMessage);
        } finally {
            setIsExporting(false);
        }
    }, [canExportPermissions, showError, showSuccess]);

    const filteredPermissions = useMemo(() => {
        const permissionsArray = Array.isArray(allPermissions) ? allPermissions : [];

        return permissionsArray.filter((permission: Permission) => {
            // Search filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    permission.name.toLowerCase().includes(query) ||
                    permission.codename.toLowerCase().includes(query) ||
                    permission.domain.toLowerCase().includes(query) ||
                    permission.action.toLowerCase().includes(query) ||
                    (permission.description && permission.description.toLowerCase().includes(query));

                if (!matchesSearch) {return false;}
            }

            // Domain filter
            if (domainFilter && permission.domain !== domainFilter.value) {
                return false;
            }

            // Role filter
            if (roleFilter?.value === 'assigned' && (!permission.assigned_roles || permission.assigned_roles.length === 0)) {
                return false;
            }
            if (roleFilter?.value === 'unassigned' && permission.assigned_roles && permission.assigned_roles.length > 0) {
                return false;
            }

            return true;
        });
    }, [allPermissions, searchQuery, domainFilter, roleFilter]);

    const handleBulkUpdate = useCallback(async (updates: Array<{id: number, name?: string}>): Promise<void> => {
        try {
            await axios.patch(window.URLS.adminPermissionsBulkUpdate, {updates});
            showSuccess(i18n.t('permissions_update_success.text', {count: updates.length}));
            // Reload permissions data
            setPermissionsReload(true);
        } catch (error: any) {
            console.error('Failed to update permissions:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to update permissions');
            showError(i18n.t('permissions_update_error.text', {error: errorMessage}));
        }
    }, [showSuccess, showError, setPermissionsReload]);

    const handlePermissionSaved = useCallback(() => {
        // Reload permissions data after role assignments change
        setPermissionsReload(true);
    }, [setPermissionsReload]);

    if (loadingUserData) {
        return <Spinner text={i18n.t('sp_loading.text')} centered fullHeight />;
    }

    if (!canViewPermissions && !loadingUserData) {
        return (
            <>
                <ConditionalNav />
                <HelpTextBlock helpText={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('permissions_name_header.text'),
            accessorKey: 'name',
            cell: ({getValue, row}: {getValue: () => string; row: {original: Permission}}) => {
                const name = getValue();
                const permission = row.original;
                return (
                    <div>
                        <div className="fw-bold">{name}</div>
                        <small className="text-muted">{permission.codename}</small>
                    </div>
                );
            },
        },
        {
            header: i18n.t('permissions_description_header.text'),
            accessorKey: 'description',
            cell: ({getValue}: {getValue: () => string}) => {
                const description = getValue();
                return (
                    <div className="text-muted small" style={{maxWidth: '200px'}}>
                        {description || i18n.t('permissions_no_description.text')}
                    </div>
                );
            },
        },
        {
            header: i18n.t('permissions_domain_header.text'),
            accessorKey: 'domain',
            cell: ({getValue}: {getValue: () => string}) => {
                const domain = getValue();

                return (
                    <Badge variant="light">
                        {domain}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('permissions_action_header.text'),
            accessorKey: 'action',
            cell: ({getValue}: {getValue: () => string}) => (
                <Badge variant="light">{getValue()}</Badge>
            ),
        },
        {
            header: i18n.t('roles_permissions.text'),
            accessorKey: 'assigned_roles',
            cell: ({getValue}: {getValue: () => string[]}) => {
                const roles = getValue() || [];
                return (
                    <Badge variant="light">
                        {roles.length} {roles.length === 1 ? i18n.t('permissions_role_singular.text') : i18n.t('permissions_role_plural.text')}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('roles_users.text'),
            accessorKey: 'user_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue();
                return (
                    <Badge variant="light">
                        {count} {count === 1 ? i18n.t('roles_user_singular.text') : i18n.t('roles_user_plural.text')}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: {row: {original: Permission}}) => {
                const permission = cell.row.original;
                const canEdit = canUpdatePermissions;

                return (
                    <div className="d-inline-block">
                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    id={`permission-dropdown-${permission.id}`}
                                    data-cy="permission-list-outlineinfo-btn"
                                />

                                <Dropdown.Menu align="end">
                                    <Dropdown.Item onClick={() => {
                                        setSelectedPermissionId(permission.id);
                                        setDetailModalOpen(true);
                                    }}>
                                        <Icon name="mdi-eye" className="mr-2" />
                                        {i18n.t('permissions_view_details.text')}
                                    </Dropdown.Item>

                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <ConditionalNav />

            <HelpTextBlock helpText={i18n.t('permissions_help_text.text')} />

            {!isModuleEnabled(userData, 'addon-advanced-user-roles') && (
                <div className="alert alert-warning">
                    {i18n.t('roles_advanced_user_roles_module_not_activated.text')}
                </div>
            )}

            <div className="mb-3 form-row">
                <Col lg={3}>
                    <SearchBox
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={i18n.t('permissions_search_placeholder.text')}
                    />
                </Col>
                <Col lg={2}>
                    <SelectField
                        name="domainFilter"
                        value={domainFilter}
                        onChange={setDomainFilter}
                        options={domainOptions}
                        placeholder={i18n.t('permissions_all_domains.text')}
                        isClearable={true}
                        isSearchable={false}
                    />
                </Col>
                <Col lg={2}>
                    <SelectField
                        name="roleFilter"
                        value={roleFilter}
                        onChange={setRoleFilter}
                        options={roleOptions}
                        placeholder={i18n.t('permissions_all_permissions.text')}
                        isClearable={true}
                        isSearchable={false}
                    />
                </Col>
                <Col lg={2}>
                    {canExportPermissions && (
                        <Button
                            variant="outline-primary"
                            onClick={handleExportPermissions}
                            size="sm"
                            disabled={isExporting}
                            data-cy="permissions-export-btn"
                        >
                            <Icon name={isExporting ? 'mdi-loading mdi-spin' : 'mdi-download'} className="mr-1" />
                            {i18n.t('permissions_export.text')}
                        </Button>
                    )}
                </Col>
                <Col lg={3} className="d-flex justify-content-end gap-2">
                    <div className="text-muted small align-self-center">
                        {i18n.t('permissions_showing_count.text', {
                            showing: filteredPermissions.length,
                            total: Array.isArray(allPermissions) ? allPermissions.length : 0
                        })}
                    </div>
                </Col>
            </div>


            {loadingPermissions ? (
                <Spinner text={i18n.t('sp_loading.text')} centered />
            ) : (

                <div className="card">
                    <div className="card-body">
                        <TableComponent data={filteredPermissions as Permission[]} columns={columns} />
                    </div>
                </div>
            )}

            <PermissionDetailModal
                isOpen={detailModalOpen}
                onClose={() => {
                    setDetailModalOpen(false);
                    setSelectedPermissionId(null);
                }}
                permissionId={selectedPermissionId}
            />
        </>
    );
});

export default PermissionsListView;
